import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate, whatsappLink, STAGES, userRoles, hasAnyRole, downloadOrderDocument } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { StatusBadge, ProgressTracker, PaymentBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Users as UsersIcon, Calendar as CalIcon, Phone, MessageCircle, Truck, ChefHat, Camera, Receipt, Upload, QrCode, FileText, Download, Pencil, Trash2, ClipboardList } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { downloadQuotation, downloadInvoice, downloadUtensilChecklist } from "@/lib/pdf";
import OrderFormDialog from "@/components/OrderFormDialog";

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [waLang, setWaLang] = useState(lang === "cn" ? "cn" : "en");
  const [order, setOrder] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState("");
  const [photoKind, setPhotoKind] = useState("setup");
  const [paymentAmt, setPaymentAmt] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [discountAmt, setDiscountAmt] = useState("");
  const [discountNote, setDiscountNote] = useState("");
  const [paymentSlip, setPaymentSlip] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState("invoice");
  const [docUploading, setDocUploading] = useState(false);
  const [invNo, setInvNo] = useState("");
  const [invDate, setInvDate] = useState("");
  const [editPay, setEditPay] = useState(null); // {id, amount, paid_at}

  // Fetch WhatsApp templates once
  useEffect(() => {
    api.get("/wa-templates")
      .then((r) => setWaTemplates(r.data || []))
      .catch((e) => console.warn("Failed to load WhatsApp templates:", e?.message));
  }, []);

  const canEdit = hasAnyRole(user, ["admin", "operations", "kitchen", "driver", "setup"]);
  const canManage = hasAnyRole(user, ["admin", "operations"]);
  const canDelete = hasAnyRole(user, ["admin"]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([api.get(`/orders/${id}`), api.get("/users/staff").catch(() => ({ data: [] }))]);
      setOrder(o.data); setStaff(s.data);
      setDiscountAmt(o.data.discount ? String(o.data.discount) : "");
      setDiscountNote(o.data.discount_note || "");
      setInvNo(o.data.invoice_number || "");
      setInvDate(o.data.invoice_date ? String(o.data.invoice_date).slice(0, 10) : "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const deleteOrder = async () => {
    if (!window.confirm(`Permanently delete order for "${order.customer_name}" (${order.tracking_code})? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      navigate("/orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const notifyStageChange = (newStage, phone) => {
    const trackUrl = `${window.location.origin}/track/${order.tracking_code}`;
    const tpl = waTemplates.find((x) => x.stage === newStage && x.is_active !== false);
    const message = tpl
      ? renderWaTemplate(tpl, order, trackUrl, lang)
      : `Hi ${order.customer_name}, status: ${newStage.replace(/_/g, " ")}. Track: ${trackUrl}`;
    const label = tpl ? ((lang === "cn" && tpl.label_cn) ? tpl.label_cn : tpl.label_en) : newStage;
    toast.success(`Moved to ${newStage.replace(/_/g, " ")}`, {
      description: `Tap to notify ${order.customer_name} via WhatsApp (${label}).`,
      action: {
        label: "Send WhatsApp",
        onClick: () => window.open(whatsappLink(phone, message), "_blank", "noopener,noreferrer"),
      },
      duration: 8000,
    });
  };

  const advance = async () => {
    const idx = STAGES.indexOf(order.status);
    const next = STAGES[Math.min(idx + 1, STAGES.length - 1)];
    await api.patch(`/orders/${id}`, { status: next });
    notifyStageChange(next, order.customer_phone);
    load();
  };

  const setStatus = async (s) => {
    await api.patch(`/orders/${id}`, { status: s });
    if (s !== order.status) {
      notifyStageChange(s, order.customer_phone);
    } else {
      toast.success("Status updated");
    }
    load();
  };

  const assign = async (k, v) => {
    await api.patch(`/orders/${id}`, { [k]: v });
    toast.success("Assignment saved");
    load();
  };

  const onPhotoFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoFile(reader.result);
    reader.readAsDataURL(f);
  };
  const onSlipFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPaymentSlip(reader.result);
    reader.readAsDataURL(f);
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    await api.post(`/orders/${id}/photos`, { data_url: photoFile, caption: photoCaption, kind: photoKind });
    setPhotoFile(null); setPhotoCaption("");
    toast.success("Photo uploaded");
    load();
  };
  const applyDiscount = async () => {
    try {
      await api.patch(`/orders/${id}`, { discount: Number(discountAmt) || 0, discount_note: discountNote });
      toast.success(Number(discountAmt) > 0 ? "Discount saved" : "Discount removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply discount"); }
  };

  const uploadDocument = async () => {
    if (!docFile) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("doc_type", docType);
      await api.post(`/orders/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocFile(null);
      document.getElementById("official-doc-input").value = "";
      const docLabel = docType === "receipt" ? "receipt" : "invoice";
      const waDocMsg = `Hi ${order.customer_name}, your official ${docLabel} for "${order.job_title || order.package_name}" is now ready. You can view & download it anytime from your HNC customer portal: ${window.location.origin}/portal (log in with your registered email). Thank you for choosing HNC Event Catering!`;
      toast.success(`Official ${docLabel} uploaded`, {
        description: `Tap to notify ${order.customer_name} via WhatsApp.`,
        action: {
          label: "Send WhatsApp",
          onClick: () => window.open(whatsappLink(order.customer_phone, waDocMsg), "_blank", "noopener,noreferrer"),
        },
        duration: 8000,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setDocUploading(false); }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/orders/${id}/documents/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const downloadDoc = async (doc) => {
    try { await downloadOrderDocument(id, doc); }
    catch { toast.error("Download failed"); }
  };

  const downloadInvoicePdf = async () => {
    let o = order;
    if (canManage && !o.invoice_number) {
      try {
        const r = await api.post(`/orders/${id}/assign-invoice-number`);
        o = { ...o, invoice_number: r.data.invoice_number };
        if (r.data.assigned) toast.success(`Invoice number assigned: ${r.data.invoice_number}`);
        load();
      } catch (e) {
        toast.info(e.response?.data?.detail || "Using auto invoice number");
      }
    }
    downloadInvoice(o);
  };

  const saveInvoiceDetails = async () => {
    try {
      await api.patch(`/orders/${id}`, { invoice_number: invNo.trim(), invoice_date: invDate });
      toast.success("Invoice details saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const savePayEdit = async () => {
    try {
      await api.patch(`/orders/${id}/payments/${editPay.id}`, { amount: Number(editPay.amount), paid_at: editPay.paid_at });
      toast.success("Payment updated");
      setEditPay(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deletePay = async (p) => {
    if (!window.confirm(`Delete payment of ${fmtCurrency(p.amount)}? The paid total will be reduced.`)) return;
    try {
      await api.delete(`/orders/${id}/payments/${p.id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const addPayment = async () => {
    if (!paymentAmt) return;
    await api.post(`/orders/${id}/payments`, { amount: Number(paymentAmt), slip_data_url: paymentSlip, method: "transfer", paid_at: paymentDate });
    setPaymentAmt(""); setPaymentSlip(null);
    toast.success("Payment recorded");
    load();
  };

  if (loading || !order) return <div className="p-10 text-zinc-500">Loading…</div>;

  const balance = (order.total_amount || 0) + (order.delivery_fee || 0) - (order.discount || 0) - (order.deposit_paid || 0);
  const trackUrl = `${window.location.origin}/track/${order.tracking_code}`;

  const downloadUtensilPdf = async () => {
    try {
      const r = await api.get(`/orders/${id}/utensils`);
      downloadUtensilChecklist(order, r.data || []);
    } catch {
      toast.error("Could not generate utensil checklist PDF");
    }
  };

  const driver = staff.find((s) => s.id === order.driver_id);
  const setupTeam = staff.filter((s) => (order.setup_team_ids || []).includes(s.id));
  const kitchenTeam = staff.filter((s) => (order.kitchen_team_ids || []).includes(s.id));

  const waMsg = `Hello ${order.customer_name}, your event "${order.package_name}" is currently: ${order.status.replace(/_/g, " ")}. Track: ${trackUrl}`;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="order-detail-page">
      <Link to="/orders" className="text-sm text-zinc-500 hover:text-[#D4AF37] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to orders
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-eyebrow">{order.event_type}</div>
          <h1 className="font-display text-4xl mt-2">{order.customer_name}</h1>
          <p className="text-zinc-500 mt-1">{order.package_name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PaymentBadge order={order} />
          <StatusBadge status={order.status} />
          {canManage && (
            <Button onClick={() => setEditOpen(true)} data-testid="edit-order-btn" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Order
            </Button>
          )}
          {canEdit && order.status !== "completed" && (
            <Button onClick={advance} data-testid="advance-status" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              Advance →
            </Button>
          )}
          {canDelete && (
            <Button onClick={deleteOrder} data-testid="delete-order-btn" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-300 rounded-sm">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column — main */}
        <div className="lg:col-span-2 space-y-4">
          {/* Event info */}
          <div className="card-lux p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Info icon={CalIcon} label="Event Date" value={fmtDate(order.event_date)} />
              <Info icon={UsersIcon} label="Pax" value={order.pax} />
              <Info icon={MapPin} label="Location" value={order.event_location} />
              {order.theme && <Info icon={Receipt} label="Theme" value={order.theme} />}
              <Info icon={Phone} label="Phone" value={order.customer_phone} />
              <Info icon={Receipt} label="Total" value={fmtCurrency(order.total_amount)} />
              <Info icon={Receipt} label="Balance" value={fmtCurrency(balance)} tint={balance > 0 ? "#F59E0B" : "#10B981"} />
            </div>
            {order.notes && (
              <div className="mt-4 pt-4 border-t border-[#1a1a1a]">
                <div className="label-eyebrow mb-1.5">Notes</div>
                <div className="text-sm text-zinc-300">{order.notes}</div>
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Menu</div>
            <h3 className="font-display text-xl mt-1 mb-4">Selection</h3>
            <div className="divide-y divide-[#1a1a1a]">
              {(order.items || []).map((it, i) => {
                const prevPkg = i > 0 ? (order.items[i - 1].package_name || "") : null;
                const curPkg = it.package_name || "";
                const showHeader = curPkg !== prevPkg && (curPkg || prevPkg !== null);
                return (
                <React.Fragment key={`${it.name}-${i}`}>
                  {showHeader && (
                    <div className="pt-3 pb-1 border-none">
                      <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-1.5" data-testid={`detail-pkg-${i}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {curPkg || "Custom items"}
                      </div>
                    </div>
                  )}
                  <div className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm">{it.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 capitalize">{it.category}</div>
                    {it.notes && <div className="text-xs text-[#D4AF37] mt-0.5" data-testid={`item-note-${i}`}>※ {it.notes}</div>}
                  </div>
                  <div className="text-sm text-zinc-400">{it.quantity ? `${it.quantity} ${it.uom || "pax"}` : `${it.pax} pax`}</div>
                </div>
                </React.Fragment>
                );
              })}
              {(!order.items || order.items.length === 0) && <div className="py-6 text-zinc-600 text-sm text-center">No menu items</div>}
            </div>
          </div>

          {/* Timeline */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Event Timeline</div>
            <h3 className="font-display text-xl mt-1 mb-4">Schedule</h3>
            <div className="space-y-2">
              {(order.timeline || []).map((tl) => (
                <div key={tl.id} className="flex items-center gap-3 py-2 border-b border-[#1a1a1a] last:border-0">
                  <input type="checkbox" checked={tl.done} disabled={!canEdit} onChange={async (e) => {
                    const newTimeline = order.timeline.map((x) => x.id === tl.id ? { ...x, done: e.target.checked } : x);
                    await api.patch(`/orders/${id}`, { timeline: newTimeline });
                    load();
                  }} className="accent-[#D4AF37]" />
                  <div className="flex-1">
                    <div className={`text-sm ${tl.done ? "line-through text-zinc-600" : "text-zinc-200"}`}>{tl.label}</div>
                    <div className="text-xs text-zinc-500">{fmtDate(tl.time)}</div>
                  </div>
                </div>
              ))}
              {(!order.timeline || order.timeline.length === 0) && <div className="py-6 text-zinc-600 text-sm text-center">No timeline yet</div>}
            </div>
          </div>

          {/* Photos */}
          <div className="card-lux p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Gallery</div>
                <h3 className="font-display text-xl mt-1">Setup & event photos</h3>
              </div>
              <Camera className="w-4 h-4 text-[#D4AF37]" />
            </div>
            {canEdit && (
              <div className="mt-4 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <select value={photoKind} onChange={(e) => setPhotoKind(e.target.value)} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
                    <option value="setup">Setup</option><option value="before">Before</option><option value="after">After</option><option value="review">Review</option>
                  </select>
                  <Input type="file" accept="image/*" onChange={onPhotoFile} data-testid="photo-input" className="bg-[#050505] border-[#2a2a2a] flex-1" />
                </div>
                <Input placeholder="Caption (optional)" value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} className="bg-[#050505] border-[#2a2a2a]" />
                <Button onClick={uploadPhoto} disabled={!photoFile} data-testid="upload-photo-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
              {(order.photos || []).map((p) => (
                <div key={p.id} className="relative aspect-square rounded-sm overflow-hidden border border-[#1a1a1a]">
                  <img src={p.data_url} alt={p.caption || ""} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] px-2 py-1 truncate">{p.caption || p.kind}</div>
                </div>
              ))}
              {(!order.photos || order.photos.length === 0) && <div className="col-span-full text-center text-zinc-600 text-sm py-6">No photos yet</div>}
            </div>
          </div>
        </div>

        {/* Right column — sidebar */}
        <div className="space-y-4">
          {/* Documents */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Documents</div>
            <h3 className="font-display text-xl mt-1 mb-3">PDF generation</h3>
            <div className="space-y-2">
              <button onClick={() => downloadQuotation(order)} data-testid="download-quotation"
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm">
                <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Quotation</span>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button onClick={downloadInvoicePdf} data-testid="download-invoice"
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm">
                <span className="flex items-center gap-2"><Receipt className="w-3.5 h-3.5" /> Invoice</span>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button onClick={downloadUtensilPdf} data-testid="download-utensil-checklist"
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm">
                <span className="flex items-center gap-2"><ClipboardList className="w-3.5 h-3.5" /> Utensil Checklist</span>
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
            {canManage && (
              <div className="mt-3 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2" data-testid="invoice-details-section">
                <div className="label-eyebrow text-[10px]">Invoice Details</div>
                <Input placeholder={`Invoice No (auto: HNC-${order.tracking_code})`} value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="invoice-number-input" className="bg-[#050505] border-[#2a2a2a]" />
                <Input type="date" title="Invoice date (blank = event date)" value={invDate} onChange={(e) => setInvDate(e.target.value)} data-testid="invoice-date-input" className="bg-[#050505] border-[#2a2a2a]" />
                <Button onClick={saveInvoiceDetails} data-testid="save-invoice-details-btn" variant="outline" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 text-xs">Save Invoice Details</Button>
                <p className="text-[10px] text-zinc-600">Used on the Invoice PDF. Leave blank to use the auto number & event date.</p>
              </div>
            )}
          </div>

          {/* Official Documents (uploaded invoices & receipts) */}
          <div className="card-lux p-6" data-testid="official-documents-card">
            <div className="label-eyebrow">Official Documents</div>
            <h3 className="font-display text-xl mt-1 mb-3">Invoices & receipts</h3>
            <div className="space-y-2">
              {(order.documents || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-sm border border-[#2a2a2a]" data-testid={`official-doc-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate flex items-center gap-1.5">
                      {d.doc_type === "receipt" ? <Receipt className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" /> : <FileText className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />}
                      <span className="truncate">{d.original_filename}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 capitalize mt-0.5">{d.doc_type} · {d.at ? d.at.slice(0, 10) : ""}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`} title="Download"
                      className="p-1.5 rounded-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    {canManage && (
                      <button onClick={() => deleteDocument(d)} data-testid={`delete-doc-${d.id}`} title="Delete"
                        className="p-1.5 rounded-sm text-zinc-500 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {(!order.documents || order.documents.length === 0) && (
                <div className="text-xs text-zinc-600 text-center py-3">No official documents uploaded yet</div>
              )}
            </div>
            {canManage && (
              <div className="mt-3 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2">
                <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select"
                  className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
                  <option value="invoice">Official Invoice</option>
                  <option value="receipt">Official Receipt</option>
                </select>
                <Input id="official-doc-input" type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="doc-file-input"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="bg-[#050505] border-[#2a2a2a]" />
                <Button onClick={uploadDocument} disabled={!docFile || docUploading} data-testid="upload-doc-btn"
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {docUploading ? "Uploading…" : "Upload Document"}
                </Button>
                <p className="text-[10px] text-zinc-600">PDF, JPG or PNG · max 10MB · visible to the customer on their portal</p>
              </div>
            )}
          </div>

          {/* Tracking + QR */}
          <div className="card-lux p-6 text-center">
            <div className="label-eyebrow">Customer Tracking</div>
            <h3 className="font-display text-xl mt-1 mb-4">Scan & Track</h3>
            <div className="bg-white p-4 rounded-sm inline-block">
              <QRCodeSVG value={trackUrl} size={140} fgColor="#050505" bgColor="#ffffff" />
            </div>
            <div className="mt-3 font-mono text-[#D4AF37] text-lg" data-testid="tracking-code">{order.tracking_code}</div>
            <Link to={`/track/${order.tracking_code}`} target="_blank" className="text-xs text-zinc-500 hover:text-[#D4AF37] mt-1 inline-block">Open tracking page →</Link>
          </div>

          {/* Progress */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Progress</div>
            <h3 className="font-display text-xl mt-1 mb-3">Live status</h3>
            <ProgressTracker status={order.status} history={order.status_history} />
            {canEdit && (
              <select value={order.status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select" className="mt-3 w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
                {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            )}
          </div>

          {/* WhatsApp */}
          <div className="card-lux p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">WhatsApp</div>
                <h3 className="font-display text-xl mt-1 mb-1">Notify customer</h3>
              </div>
              <div className="flex rounded-sm border border-[#2a2a2a] overflow-hidden" data-testid="wa-lang-toggle">
                {[["en", "EN"], ["cn", "中文"]].map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => setWaLang(k)} data-testid={`wa-lang-${k}`}
                    className={`px-2.5 py-1 text-[11px] transition-colors ${waLang === k ? "bg-[#D4AF37] text-black font-medium" : "text-zinc-400 hover:text-white"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Templates auto-match each event stage. Current stage is highlighted. Manage templates in Settings.</p>
            <div className="space-y-2 text-sm">
              {waTemplates.length === 0 ? (
                <div className="text-xs text-zinc-500 py-2">Loading templates…</div>
              ) : (
                waTemplates.filter((tpl) => tpl.is_active !== false).map((tpl) => {
                  const rendered = renderWaTemplate(tpl, order, trackUrl, waLang);
                  const active = tpl.stage && tpl.stage === order.status;
                  const label = (waLang === "cn" && tpl.label_cn) ? tpl.label_cn : tpl.label_en;
                  return (
                    <WAButton
                      key={tpl.id}
                      phone={order.customer_phone}
                      msg={rendered}
                      label={label}
                      active={active}
                      testidKey={tpl.stage || tpl.id}
                    />
                  );
                })
              )}
              <WAButton
                phone={order.customer_phone}
                msg={waMsg}
                label="Custom status update"
                testidKey="custom"
              />
            </div>
          </div>

          {/* Assignments */}
          {canManage && (
            <div className="card-lux p-6">
              <div className="label-eyebrow">Assignments</div>
              <h3 className="font-display text-xl mt-1 mb-3">Team</h3>
              <Label className="text-xs text-zinc-500">Driver</Label>
              <select value={order.driver_id || ""} onChange={(e) => assign("driver_id", e.target.value || null)} data-testid="assign-driver" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm mb-3 mt-1">
                <option value="">— None —</option>
                {staff.filter((s) => userRoles(s).includes("driver")).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Label className="text-xs text-zinc-500">Setup Team</Label>
              <MultiSelect options={staff.filter((s) => userRoles(s).includes("setup"))} value={order.setup_team_ids || []} onChange={(v) => assign("setup_team_ids", v)} testid="assign-setup" />
              <Label className="text-xs text-zinc-500 mt-3 block">Kitchen Team</Label>
              <MultiSelect options={staff.filter((s) => userRoles(s).includes("kitchen"))} value={order.kitchen_team_ids || []} onChange={(v) => assign("kitchen_team_ids", v)} testid="assign-kitchen" />
            </div>
          )}

          {/* Payments */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Payments</div>
            <h3 className="font-display text-xl mt-1 mb-3">Ledger</h3>
            <div className="space-y-2">
              {(order.payments || []).map((p) => (
                <div key={p.id} className="py-2 border-b border-[#1a1a1a] last:border-0" data-testid={`payment-row-${p.id}`}>
                  {editPay?.id === p.id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" step="0.01" value={editPay.amount} onChange={(e) => setEditPay({ ...editPay, amount: e.target.value })} data-testid="edit-payment-amount" className="bg-[#050505] border-[#2a2a2a] h-8 text-sm" />
                        <Input type="date" value={editPay.paid_at} onChange={(e) => setEditPay({ ...editPay, paid_at: e.target.value })} data-testid="edit-payment-date" className="bg-[#050505] border-[#2a2a2a] h-8 text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={savePayEdit} disabled={!editPay.amount || Number(editPay.amount) <= 0} data-testid="save-payment-edit" className="flex-1 h-7 text-xs bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Save</Button>
                        <Button onClick={() => setEditPay(null)} variant="outline" className="flex-1 h-7 text-xs border-[#2a2a2a]">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <div className="text-zinc-200">{fmtCurrency(p.amount)}</div>
                        <div className="text-xs text-zinc-500 capitalize">{p.method} · {p.paid_at ? p.paid_at.slice(0, 10) : fmtDate(p.at)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {p.slip_data_url && <a href={p.slip_data_url} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] mr-1">View slip</a>}
                        {canManage && (
                          <>
                            <button onClick={() => setEditPay({ id: p.id, amount: String(p.amount), paid_at: p.paid_at ? p.paid_at.slice(0, 10) : "" })} data-testid={`edit-payment-${p.id}`} title="Edit payment"
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-[#D4AF37] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deletePay(p)} data-testid={`delete-payment-${p.id}`} title="Delete payment"
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.01" placeholder="Amount RM" value={paymentAmt} onChange={(e) => setPaymentAmt(e.target.value)} data-testid="payment-amount" className="bg-[#050505] border-[#2a2a2a]" />
                <Input type="date" title="Payment date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} data-testid="payment-date" className="bg-[#050505] border-[#2a2a2a]" />
              </div>
              <Input type="file" accept="image/*" onChange={onSlipFile} className="bg-[#050505] border-[#2a2a2a]" />
              <Button onClick={addPayment} disabled={!paymentAmt} data-testid="add-payment-btn" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Record Payment</Button>
            </div>
            {canManage && (
              <div className="mt-3 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2" data-testid="discount-section">
                <div className="label-eyebrow text-[10px]">Discount</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min="0" step="0.01" placeholder="Amount RM" value={discountAmt} onChange={(e) => setDiscountAmt(e.target.value)} data-testid="discount-amount" className="bg-[#050505] border-[#2a2a2a]" />
                  <Input placeholder="Reason (optional)" value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} data-testid="discount-note" className="bg-[#050505] border-[#2a2a2a]" />
                </div>
                <Button onClick={applyDiscount} disabled={discountAmt === "" && !(order.discount > 0)} data-testid="apply-discount-btn" variant="outline" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">{order.discount > 0 ? "Update Discount" : "Apply Discount"}</Button>
              </div>
            )}
            <div className="mt-3 space-y-1.5">
              {(order.delivery_fee || 0) > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="delivery-fee-row">
                  <span className="text-zinc-500">Delivery fee</span>
                  <span className="text-zinc-300">{fmtCurrency(order.delivery_fee)}</span>
                </div>
              )}
              {(order.discount || 0) > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="discount-row">
                  <span className="text-zinc-500">Discount{order.discount_note ? ` — ${order.discount_note}` : ""}</span>
                  <span className="text-emerald-400">-{fmtCurrency(order.discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">Balance due (HNC ledger)</span>
                <span className={`font-medium ${balance > 0 ? "text-amber-400" : "text-emerald-500"}`}>{fmtCurrency(balance)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <OrderFormDialog
        open={editOpen}
        editing={editOpen ? order : null}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />
    </div>
  );
}

function Info({ icon: Icon, label, value, tint }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-sm mt-1" style={{ color: tint || "#FAFAFA" }}>{value}</div>
    </div>
  );
}

// Replace placeholders in a WhatsApp template with the order's actual values.
// Placeholders: {customer_name}, {package_name}, {event_date}, {pax}, {tracking_url},
// {tracking_code}, {balance}, {total}.
function renderWaTemplate(tpl, order, trackUrl, lang = "en") {
  const raw = (lang === "cn" && tpl.message_cn) ? tpl.message_cn : (tpl.message_en || "");
  const date = order?.event_date
    ? new Date(order.event_date).toLocaleString(lang === "cn" ? "zh-CN" : "en-MY", { dateStyle: "medium", timeStyle: "short" })
    : "";
  const balance = ((order?.total_amount || 0) + (order?.delivery_fee || 0) - (order?.discount || 0) - (order?.deposit_paid || 0)).toFixed(2);
  const replacements = {
    "{customer_name}": order?.customer_name || "",
    // Job Title takes priority in customer-facing messages; falls back to internal package name
    "{package_name}": order?.job_title || order?.package_name || "",
    "{job_title}": order?.job_title || order?.package_name || "",
    "{event_date}": date,
    "{pax}": String(order?.pax || ""),
    "{tracking_url}": trackUrl || "",
    "{tracking_code}": order?.tracking_code || "",
    "{balance}": `RM${balance}`,
    "{total}": `RM${(order?.total_amount || 0).toFixed(2)}`,
  };
  let out = raw;
  for (const [k, v] of Object.entries(replacements)) {
    out = out.split(k).join(v);
  }
  return out;
}

function WAButton({ phone, msg, label, active, testidKey }) {
  const tid = testidKey ? `wa-stage-${testidKey}` : `wa-${label.replace(/\s+/g, '-')}`;
  return (
    <a href={whatsappLink(phone, msg)} target="_blank" rel="noreferrer" data-testid={tid}
       className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border transition-colors text-sm ${active
         ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5 ring-1 ring-[#D4AF37]/40"
         : "border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
      <span className="flex items-center gap-2">
        <MessageCircle className="w-3.5 h-3.5" />
        {label}
        {active && <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]/80">· current stage</span>}
      </span>
      <span className="text-xs">→</span>
    </a>
  );
}

function MultiSelect({ options, value, onChange, testid }) {
  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {options.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="accent-[#D4AF37]" />
          {o.name}
        </label>
      ))}
      {options.length === 0 && <div className="text-xs text-zinc-600">No staff available</div>}
    </div>
  );
}

